import React, { useState, useRef, useEffect } from 'react';

const optionToSlug = (option) => String(option).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const SortByDropdown = ({
  orderOptions = [],
  sortByOptions = [],
  selectedOrder = "",
  selectedSortBy = "",
  onApply,
  onReset,
  "data-testid": dataTestId = "sort-by-dropdown",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const [order, setOrder] = useState(selectedOrder);
  const [sortBy, setSortBy] = useState(selectedSortBy);

  const toggleDropdown = () => {
    if (isOpen) {
      setIsOpen(false);
      return;
    }
    setOrder(selectedOrder);
    setSortBy(selectedSortBy);
    setIsOpen(true);
  };

  const onSubmit = () => {
    onApply?.({ order, sortBy });
  }

  const clearAll = () => {
    setOrder('');
    setSortBy('');
    onReset?.();
    setIsOpen(false);
  }

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const renderOption = (option, selected, onSelect, idPrefix) => (
    <li
      key={option.value}
      data-testid={`${dataTestId}-${idPrefix}-option-${optionToSlug(option.label)}`}
      className={`px-3 py-2.5 text-black dark:text-white text-xs text-left leading-4 lg:text-sm lg:leading-5 font-normal tracking-[-0.408px] border-b border-grey dark:border-darkThemeBorder cursor-pointer
        ${selected === option.value ? 'bg-[#FFF1C6] dark:bg-darkThemeDropdownItemBg flex justify-between items-center gap-2' : ''}`}
      onClick={(e) => {
        e.stopPropagation(); // Prevent dropdown toggle
        onSelect(option.value); // Only select, do not close
      }}
    >
      <span>{option.label}</span>
      {selected === option.value && (
        <span>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M8.49215 15C8.24102 15 8.00244 14.8995 7.82665 14.7238L4.27311 11.1702C3.90896 10.8061 3.90896 10.2033 4.27311 9.8392C4.63725 9.47506 5.23997 9.47506 5.60412 9.8392L8.49215 12.7272L14.9463 6.27311C15.3104 5.90896 15.9132 5.90896 16.2773 6.27311C16.6414 6.63725 16.6414 7.23997 16.2773 7.60412L9.15766 14.7238C8.98186 14.8995 8.74329 15 8.49215 15Z"
              fill="#FF8C65"
            />
          </svg>
        </span>
      )}
    </li>
  );

  return (
    <div
      className="sortingHolder h-9 basis-auto sm:basis-44 rounded-md bg-[#FFFFFFDE] dark:darkThemeGradiantBg justify-between shadow-appShadow flex flex-1 group relative sm:px-3 sm:py-2 gap-2  px-2 py-1"
      ref={dropdownRef}
      data-testid={dataTestId}
    >
      <div
        className="bg-white dark:bg-transparent dark:text-white grow leading-5 text-sm font-medium shadow-none text-dark-grey tracking-[0.021px] cursor-pointer hidden sm:flex"
        onClick={toggleDropdown}
        data-testid={`${dataTestId}-trigger`}
      >
        Sort by
      </div>
      <div className="dropdownIconHolder static h-full sm:flex items-center cursor-pointer hidden" onClick={toggleDropdown}>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M19 9L12 15L5 9" stroke="black" strokeOpacity="0.87" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className='dark:stroke-white'/>
        </svg>
      </div>
      <div className="iconHolder  items-center justify-center flex sm:hidden" onClick={toggleDropdown} data-testid={`${dataTestId}-trigger-mobile`}>
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" className='*:dark:stroke-white'>
          <path d="M20 5L0 5" stroke="#1E2A2C" strokeWidth="1.5" strokeLinecap="round" />
          <path d="M17 10L3 10" stroke="#1E2A2C" strokeWidth="1.5" strokeLinecap="round" />
          <path d="M14 15L6 15" stroke="#1E2A2C" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </div>

      {isOpen && (
        <div className="dropdownMenuHolder absolute bg-white dark:bg-darkThemeDropdownBg dark:shadow-darkThemeDropdownMenuShadow top-full mt-2 right-0 shadow-appShadow rounded-md w-max z-50 max-h-[70vh] overflow-y-auto" data-testid={`${dataTestId}-menu`}>
          <h5 className="sortingCatagryTitle text-dark-grey dark:text-white text-xs font-normal leading-6 tracking-[0.018px] px-3 my-2">Order:</h5>
          <ul className="bg-white dark:bg-transparent rounded-lg w-full">
            {orderOptions.map((option) => renderOption(option, order, setOrder, "order"))}
          </ul>

          <h5 className="sortingCatagryTitle text-dark-grey dark:text-white text-xs font-normal leading-6 tracking-[0.018px] px-3 my-2">Sort by:</h5>
          <ul className="bg-white dark:bg-transparent rounded-lg w-full">
            {sortByOptions.map((option) => renderOption(option, sortBy, setSortBy, "sort-by"))}
          </ul>

          <div className="dropdownActionsHolder px-3 py-4 flex justify-between">
            <button
              className="text-theme h-7 text-xs font-semibold leading-4 tracking-[0.15] hover:text-[#FF8C65] px-5 py-1 rounded"
              data-testid={`${dataTestId}-reset-button`}
              onClick={(e) => {
                e.stopPropagation();
                clearAll()
              }}
            >
              Reset
            </button>
            <button
              className="text-white h-7 text-xs font-semibold leading-4 tracking-[0.15] bg-theme px-5 py-1 rounded hover:bg-theme/80"
              data-testid={`${dataTestId}-apply-button`}
              onClick={(e) => {
                e.stopPropagation();
                onSubmit();
                setIsOpen(false);
              }}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SortByDropdown;
